import { useEffect, useRef, useState } from "react";
import { ArrowDownIcon } from "lucide-react";
import useEventCallback from "../../hooks/useEventCallback";

const MessagesContainer = ({ children, messageLoading, autoScroll = true }) => {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [messageChanged, setMessageChanged] = useState(false);
  const scrollToBottom = useEventCallback(() => {
    if (!userHasScrolled && messagesEndRef.current && autoScroll) {
      setMessageChanged(true);
    }
    if (isNearBottom()) {
      setMessageChanged(false);
    }
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount

    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current && autoScroll) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" }); // Scroll to bottom on mount
      }
    }, 500);
    
    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('resize', handleResize);
    }
  }, [autoScroll])

  // Effect for automatic scrolling
  useEffect(() => {
    scrollToBottom();
  }, [children, messageLoading, scrollToBottom]);
  
  const getIsMobileOnly = () => {
    return windowWidth < 768
  }
  
  // Function to check if user is near bottom
  const isNearBottom = () => {
    if (!containerRef.current) return true;
    
    const container = containerRef.current;
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  // Handle scroll events
  const handleScroll = () => {
    if (!messageLoading) {
      setUserHasScrolled(!isNearBottom());
    }
    if (isNearBottom()) {
      setMessageChanged(false);
    }
  };

  const moveToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }

  return (
    <div 
      ref={containerRef}
      className={`flex flex-grow flex-col w-full p-5 rounded-lg border-t-2 border-slate-200 overflow-y-auto ${getIsMobileOnly() ? 'mb-0' : 'mb-44'}`}
      onScroll={handleScroll}
    >
      <div className="flex flex-col w-full relative text-base">
        {children}
        {messageChanged && (
          <div className="flex sticky bottom-0 justify-center items-center w-full">
            <button 
              onClick={moveToBottom}
              className="flex justify-center text-white p-2 items-center w-10 h-10 bg-blue-500 hover:bg-blue-600 rounded-full animate-bounce"
            >
              <ArrowDownIcon />
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessagesContainer;
